import { format, subMinutes } from 'date-fns'
import { useEffect, useState } from 'react'

import { BasicTrade, useGetInstrumentBookFull } from '@/gen/marketDataApi'

import { GRAPH_MINUTE_FORMAT } from '@/utils/time'

import HistoryChart, { DataPoint } from './chart'

interface Props {
    clientCode: string
    marketCode: string
    instrumentSymbol: string
    minuteValue: number
}

export default function MinuteHistory({ minuteValue, clientCode, marketCode, instrumentSymbol }: Props) {
    const full = useGetInstrumentBookFull(clientCode, marketCode, instrumentSymbol, {
        query: { refetchInterval: 5000 },
    })

    const [data, setData] = useState<DataPoint[]>([])
    useEffect(() => {
        if (full.isLoading && data.length) {
            // We're reloading the data from the server, so clear the local data
            setData([])
        }

        if (!full.data) {
            return
        }

        // Filter the data to only the trades that happened in the last minuteValue
        // minutes
        const minutesAgo = subMinutes(new Date(), minuteValue)
        const filteredData = full.data.recentTrades.filter(p => {
            const date = new Date(p.timestampUtc)
            return date >= minutesAgo
        })

        // If the value is the same as the data, ignore. Nothing to do
        if (filteredData.length === data.length) {
            return
        }

        const mappedData: DataPoint[] = filteredData.map((p: BasicTrade) => ({
            time: format(new Date(p.timestampUtc), GRAPH_MINUTE_FORMAT),
            price: p.price,
        }))

        setData(mappedData)
    }, [data, minuteValue, full.data, full.isLoading])

    if (full.isLoading || !full.data) {
        return null
    }

    return <HistoryChart data={data} />
}
